import React, { useEffect, useState } from 'react';
import chatSocketService, { ConnectionState } from '@/api/chat-socket-service';

const WebSocketDebugger: React.FC = () => {
  const [connectionState, setConnectionState] = useState<ConnectionState>(chatSocketService.getState());
  const [connectionHistory, setConnectionHistory] = useState<{time: string, event: string}[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Monitor connection state changes
  useEffect(() => {
    const handleStateChange = (state: ConnectionState) => {
      setConnectionState(state);
      const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
      setConnectionHistory(prev => [...prev, {
        time: timestamp,
        event: `State changed to ${state}`
      }]);
    };

    chatSocketService.onStateChange(handleStateChange);
    
    // Log initial state
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    setConnectionHistory([{
      time: timestamp,
      event: `Initial state: ${chatSocketService.getState()}`
    }]);

    return () => {
      chatSocketService.removeStateChangeListener(handleStateChange);
    };
  }, []);

  // Force connection test
  const handleForceConnect = () => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    setConnectionHistory(prev => [...prev, {
      time: timestamp,
      event: 'Manually triggered connect()'
    }]);
    chatSocketService.connect();
  };

  // Force reconnection test
  const handleResetConnection = () => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    setConnectionHistory(prev => [...prev, {
      time: timestamp,
      event: 'Manually triggered resetConnectionState()'
    }]);
    chatSocketService.resetConnectionState();
  };

  // Manual disconnect test
  const handleDisconnect = () => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    setConnectionHistory(prev => [...prev, {
      time: timestamp,
      event: 'Manually triggered disconnect()'
    }]);
    chatSocketService.disconnect();
  };

  const getStateColor = () => {
    switch(connectionState) {
      case ConnectionState.CONNECTED: return 'bg-green-500';
      case ConnectionState.CONNECTING: return 'bg-yellow-500';
      case ConnectionState.RECONNECTING: return 'bg-orange-500';
      case ConnectionState.DISCONNECTED: return 'bg-gray-500';
      case ConnectionState.ERROR: return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white shadow-lg rounded-lg p-3 text-xs w-64">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full mr-2 ${getStateColor()}`}></div>
          <span className="font-bold">WebSocket: {connectionState}</span>
        </div>
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-500 hover:text-gray-700"
        >
          {isExpanded ? '▼' : '▲'}
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-2">
          <div className="flex space-x-1 mb-2">
            <button 
              onClick={handleForceConnect}
              className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
            >
              Connect
            </button>
            <button 
              onClick={handleResetConnection}
              className="bg-orange-500 text-white px-2 py-1 rounded text-xs"
            >
              Reset
            </button>
            <button 
              onClick={handleDisconnect}
              className="bg-red-500 text-white px-2 py-1 rounded text-xs"
            >
              Disconnect
            </button>
          </div>
          
          <div className="h-32 overflow-y-auto border border-gray-200 rounded p-1">
            {connectionHistory.map((entry, index) => (
              <div key={index} className="text-xs mb-1">
                <span className="text-gray-500">{entry.time}</span>: {entry.event}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebSocketDebugger; 